export const FOUND_STATUS_LABELS = {
    found: { text: 'Found', color: 'green', basic: false },
    not_found: { text: 'Not found', color: 'grey', basic: true },
    no_search_results: {
        text: 'No search results',
        color: 'grey',
        basic: true,
    },
}

export const MATCH_STATUS_LABELS = {
    same_person: { text: 'Same person', color: 'green', basic: false },
    different_person: { text: 'Not this person', color: 'grey', basic: true },
    not_checked: { text: 'Not checked', color: 'yellow', basic: true },
}

// Review order for a person's web sources: the pages judged to be them first,
// then the ones nothing has checked. Sorting is stable, so within each group the
// order the searches returned stays intact and the search engine's own ranking
// is still visible.
const MATCH_STATUS_ORDER = {
    same_person: 0,
    not_checked: 1,
    different_person: 2,
}

export const byMatchStatus = (first, second) =>
    (MATCH_STATUS_ORDER[first.matchStatus] ?? 9) -
    (MATCH_STATUS_ORDER[second.matchStatus] ?? 9)

// The pipeline's own reason for an unchecked page is terse; this is the same
// fact with the why a reviewer needs.
const NOT_CHECKED_EXPLANATION =
    'Not checked. Only the first few results of each search are checked, ' +
    'to keep a run from taking hours — this page was further down the list.'

export const matchExplanation = (page = {}) =>
    page.matchStatus === 'not_checked'
        ? NOT_CHECKED_EXPLANATION
        : page.matchReason

// The registry field names as a person would say them.
const FACT_NAMES = {
    lastName: 'last name',
    firstName: 'first name',
    city: 'city',
    specialty: 'specialty',
}

export const factName = (fact) => FACT_NAMES[fact] || fact

// "bing: Suspended: too many requests" is one string from SearXNG; the engine
// is everything before the first colon.
export const splitEngineReason = (entry) => {
    const at = entry.indexOf(':')

    return at === -1
        ? { engine: entry, reason: '' }
        : {
              engine: entry.slice(0, at).trim(),
              reason: entry.slice(at + 1).trim(),
          }
}

// What sort of site a page is, in words a sourcer would use.
export const KIND_LABELS = {
    profile: 'professional directory',
    institution: 'employer or university',
    registry_directory: 'copy of the NPI registry',
    other: 'other',
}

export const SEARCH_LABELS = {
    general: 'General search',
    linkedin: 'LinkedIn search',
    employer: 'Employer search',
}

export const displayName = (expert = {}) => expert.fullName || 'Unknown'

// An external expert carries exactly one registration: the registry record
// they were found in.
export const registrationOf = (expert = {}) =>
    (expert.professionalRegistrations || [])[0] || {}

// The terms a reviewer is checking each page against. Highlighting these is
// what makes a page scannable in seconds rather than read in a minute: the eye
// goes to the surname, the city and the specialty without hunting for them.
export const termsToHighlight = (expert = {}) => {
    const words = [
        expert.firstName,
        expert.lastName,
        expert.city,
        ...(expert.specialties || []).flatMap((value) =>
            value.split(/[^A-Za-z]+/)
        ),
        ...(expert.organizations || []).flatMap((value) =>
            value.split(/[^A-Za-z]+/)
        ),
    ]

    // Short words match inside longer ones and turn a snippet into stripes.
    return [...new Set(words.filter((word) => word && word.length > 3))]
}

// How much of a fetched page is shown before a reviewer asks for the rest.
export const PAGE_TEXT_PREVIEW_LENGTH = 1500

// How far from either edge of a preview a word boundary still counts as one.
const EDGE_SLACK = 40

// The part of a page that concerns this person: a window around the first place
// their surname appears. A department page runs to thousands of characters
// covering everyone on it, and the paragraph about one person is the part worth
// reading first.
//
// A preview, not a limit. The pipeline stores the whole page, and this decides
// only where the reader starts — the rest is one click away rather than thrown
// out, which is what makes a past post or a publication list findable at all.
export const previewAround = (text, needle, window) => {
    if (!text) return ''
    if (text.length <= window) return text

    const at = needle ? text.toLowerCase().indexOf(needle.toLowerCase()) : -1
    // A page that never spells the surname out has nowhere better to start than
    // the beginning. It used to be dropped outright, which was right when this
    // text fed a model and wrong now that a person reads it.
    const from = at < 0 ? 0 : Math.max(0, at - Math.floor(window / 2))
    const slice = text.slice(from, from + window)

    // Pulled back to whitespace at both ends, so a preview does not open or
    // close mid-word — but only where there is whitespace near the edge to pull
    // back to. A long unbroken run, a base64 image or a minified script that
    // survived tag stripping, would otherwise trim the preview to almost nothing.
    const opens = from === 0 ? 0 : slice.search(/\s/) + 1
    const closes = slice.lastIndexOf(' ')
    const start = opens <= EDGE_SLACK ? opens : 0
    const end = closes >= slice.length - EDGE_SLACK ? closes : slice.length

    return slice.slice(start, end > start ? end : undefined).trim()
}

const escapeForRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Splits text into plain and matched runs, so the caller can mark the matches
// without putting a string through dangerouslySetInnerHTML.
export const splitOnTerms = (text, terms) => {
    if (!text) return []
    if (terms.length === 0) return [{ text, match: false }]

    const pattern = new RegExp(`(${terms.map(escapeForRegex).join('|')})`, 'gi')

    return text
        .split(pattern)
        .filter((part) => part !== '')
        .map((part) => ({
            text: part,
            match: terms.some(
                (term) => term.toLowerCase() === part.toLowerCase()
            ),
        }))
}
